import React from 'react';
import { Card, cn } from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';
import type { AgentKeyFindingsOutput, AgentStep } from '../../types';
import { AgentKeyFindingsCard } from './AgentKeyFindingsCard';
import { AgentThoughtsRunDialog } from './AgentThoughtsRunDialog';
import { StepStatusDot } from './AgentThoughtsShared';

export interface AgentThoughtsCardProps {
  steps: AgentStep[];
  /**
   * Label above the step list (e.g. "Agent thoughts").
   */
  label?: string;
  /**
   * CTA that opens the full timeline dialog.
   */
  detailButtonLabel?: string;
  /**
   * Optional dialog title when viewing all steps.
   */
  dialogTitle?: string;
  /**
   * Optional dialog description under the title.
   */
  dialogDescription?: string;
  className?: string;
  /**
   * Optional override for rendering each step's detail body (same as AgentStepDetail).
   */
  renderStepContentOverride?: (step: AgentStep) => React.ReactNode;
  /**
   * Optional post-run block: key findings and recommendations shown below the compact card.
   */
  keyFindings?: AgentKeyFindingsOutput;
  /**
   * Custom handler when a key-finding Deep dive pill is pressed. Defaults to opening the run dialog scrolled to `sourceStepId`.
   */
  onActionSourceClick?: (sourceStepId: string) => void;
  /** Label for deep-dive pills on key findings (default `Deep dive`). */
  actionSourceLabel?: string;
}

export const AgentThoughtsCard: React.FC<AgentThoughtsCardProps> = ({
  steps: stepsProp,
  label = 'Agent thoughts',
  detailButtonLabel = 'View in detail',
  dialogTitle = 'Agent run',
  dialogDescription,
  className,
  renderStepContentOverride,
  keyFindings,
  onActionSourceClick: onActionSourceClickOverride,
  actionSourceLabel,
}) => {
  const [open, setOpen] = React.useState(false);
  const [runDialogInitialStepId, setRunDialogInitialStepId] = React.useState<string | undefined>();
  /** Storybook Docs / Controls can omit `steps` before args hydrate. */
  const steps = stepsProp ?? [];

  const showKeyFindings = Boolean(keyFindings?.findings?.length);

  const handleRunDialogOpenChange = React.useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setRunDialogInitialStepId(undefined);
  }, []);

  const handleActionSource = React.useCallback(
    (sourceStepId: string) => {
      if (onActionSourceClickOverride) {
        onActionSourceClickOverride(sourceStepId);
        return;
      }
      setRunDialogInitialStepId(sourceStepId);
      setOpen(true);
    },
    [onActionSourceClickOverride],
  );

  return (
    <>
      <div
        className={cn(
          'mx-auto flex w-full max-w-[600px] min-w-0 flex-col gap-6',
          showKeyFindings ? '' : 'contents',
        )}
      >
      <Card
        className={cn(
          'relative w-full min-w-[600px] max-w-full overflow-hidden rounded-xl border-custom-border shadow-sm',
          className,
        )}
      >
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-rose-100/80 via-orange-50/40 to-transparent dark:from-rose-950/30 dark:via-orange-950/20"
          aria-hidden
        />

        <div className="relative z-10 p-4 pr-6">
          <p className="text-[11px] font-medium uppercase tracking-wide text-rose-600/90 dark:text-rose-400/90">
            {label}
          </p>

          <ul className="mt-3 space-y-2.5">
            {steps.map((step) => (
              <li key={step.id} className="flex gap-2.5">
                <StepStatusDot status={step.status} />
                <span className="text-sm leading-snug text-custom-text-primary">
                  {step.title}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setRunDialogInitialStepId(undefined);
                setOpen(true);
              }}
            >
              {detailButtonLabel}
            </Button>
          </div>
        </div>
      </Card>

      {showKeyFindings && keyFindings ? (
        <AgentKeyFindingsCard
          title={keyFindings.title}
          layout={keyFindings.layout}
          findings={keyFindings.findings}
          onActionSourceClick={handleActionSource}
          actionSourceLabel={actionSourceLabel}
        />
      ) : null}
      </div>

      <AgentThoughtsRunDialog
        open={open}
        onOpenChange={handleRunDialogOpenChange}
        steps={steps}
        dialogTitle={dialogTitle}
        dialogDescription={dialogDescription}
        renderStepContentOverride={renderStepContentOverride}
        initialScrollStepId={runDialogInitialStepId}
      />
    </>
  );
};
